import React from 'react';
import styles from './PageLoader.module.scss';

export interface PageLoaderProps {
    /** Loading message title */
    title?: string;
    /** Loading message subtitle */
    subtitle?: string;
    /** Variant of the loader */
    variant?: 'full' | 'inline' | 'simple' | 'skeleton';
    /** Size for simple spinner variant */
    size?: 'small' | 'medium' | 'large';
    /** Whether to show fullscreen overlay */
    fullscreen?: boolean;
    /** Additional CSS class */
    className?: string;
}

const PageLoader: React.FC<PageLoaderProps> = ({
    title = 'Memuat...',
    subtitle = 'Mohon tunggu sebentar',
    variant = 'full',
    size = 'medium',
    fullscreen = false,
    className = '',
}) => {
    // Simple spinner variant
    if (variant === 'simple') {
        const sizeClass = {
            small: styles.simpleSpinnerSmall,
            medium: '',
            large: styles.simpleSpinnerLarge,
        }[size];

        return (
            <div
                className={`${styles.loaderContainer} ${styles.loaderInline} ${className}`}
                role="status"
                aria-label={title}
            >
                <div className={`${styles.simpleSpinner} ${sizeClass}`} />
            </div>
        );
    }

    // Skeleton loading variant
    if (variant === 'skeleton') {
        return (
            <div
                className={`${styles.loaderContainer} ${styles.loaderInline} ${className}`}
                role="status"
                aria-label={title}
            >
                <div className={styles.skeletonWrapper}>
                    <div className={styles.skeletonLine} />
                    <div className={styles.skeletonLine} />
                    <div className={styles.skeletonLine} />
                    <div className={styles.skeletonLine} />
                </div>
            </div>
        );
    }

    // Main container class based on variant
    const containerClass = [
        styles.loaderContainer,
        variant === 'inline' && styles.loaderInline,
        fullscreen && styles.loaderFullscreen,
        className,
    ].filter(Boolean).join(' ');

    return (
        <div
            className={containerClass}
            role="status"
            aria-label={title}
        >
            <div className={styles.loaderContent}>
                {/* Heart icon with spinner ring */}
                <div className={styles.iconWrapper}>
                    <div className={styles.spinnerRingSecondary} />
                    <div className={styles.spinnerRing} />
                </div>

                {/* Loading text */}
                <div className={styles.loaderText}>
                    <h2 className={styles.loaderTitle}>{title}</h2>
                    {subtitle && <p className={styles.loaderSubtitle}>{subtitle}</p>}
                </div>

                {/* Progress dots */}
                <div className={styles.progressDots}>
                    <span className={styles.progressDot} />
                    <span className={styles.progressDot} />
                    <span className={styles.progressDot} />
                </div>
            </div>
        </div>
    );
};

export default PageLoader;
